/**
 * `ASSIGN_DAILY` step. Batches workOrderIds (chunk size 200) and calls
 * `deps.assignDaily` with `skipInvalidate: true` on each batch — the
 * executor's terminal INVALIDATE step is the single cache flush.
 */
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { RouteId, ServiceDate, WorkOrderId } from 'types/entities/shared/ids'
import type {
  AssignDailyResponse,
  Deps,
  PipelineStep,
} from 'types/modules/routing'

import { buildSequencePairs } from '../payloads'
import type { StepResult } from '../types'

const BATCH_SIZE = 200

export interface AssignDailyStepArgs {
  readonly routeId: RouteId
  readonly serviceDate: ServiceDate
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
  readonly startSequence?: number
}

export type AssignDailyStepValue = {
  readonly newStopCount: number
  readonly batchCount: number
}

export type AssignDailyStepResult = StepResult<'ASSIGN_DAILY', AssignDailyStepValue>

export const makeAssignDailyStep = (
  args: AssignDailyStepArgs,
  deps: Deps,
): PipelineStep<'ASSIGN_DAILY', AssignDailyStepResult> => {
  const stepId = `assign-daily:${args.routeId}:${args.serviceDate}`
  return {
    id: stepId,
    phase: 'ASSIGN_DAILY',
    label: 'Failed to assign stops to daily schedule',
    applyOrder: 100,
    run: async (ctx) => {
      if (args.workOrderIds.length === 0) {
        return ok({
          phase: 'ASSIGN_DAILY',
          stepId,
          value: { newStopCount: 0, batchCount: 0 },
        })
      }
      const fullPairs = buildSequencePairs(
        args.workOrderIds.map(id => ({ id: String(id) })),
        'id',
        args.startSequence ?? 1,
      ).pairs
      const batches = chunk(fullPairs, BATCH_SIZE)
      let newStopCount = 0
      try {
        for (const batch of batches) {
          const response: AssignDailyResponse = await deps
            .assignDaily({
              routeId: args.routeId,
              data: {
                serviceDate: args.serviceDate,
                stops: batch.map(p => ({ stopId: String(p.stopId), sequence: p.sequence })),
                versionContext: ctx.versionContext,
              },
              skipInvalidate: true,
            })
            .unwrap()
          newStopCount += response?.newStopCount ?? 0
        }
        return ok({
          phase: 'ASSIGN_DAILY',
          stepId,
          value: { newStopCount, batchCount: batches.length },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'ASSIGN_DAILY',
          status,
          message,
        })
      }
    },
  }
}
